import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";
import { staleGuard, dedupeInFlight } from "src/utils/staleGuard";

// Centro de errores AEAT/VeriFactu del restaurante. Cubre listado paginado
// con filtros, contador para el badge, reintento individual/bulk y ack.
const initialState = {
  isLoading: false,
  items: [],
  total: 0,
  unacknowledgedTotal: 0,
  badgeCount: 0, // contador rápido (separado del listado)
  // 2026-10-27 · Auditoría Hacienda: último resumen (GET AeatAudit/Summary)
  // { byState, errors, retrying, total } — solo del restaurante de la sesión.
  auditSummary: null,
  error: null,
};

const slice = createSlice({
  name: "aeatErrors",
  initialState,
  reducers: {
    startLoading(state) { state.isLoading = true; state.error = null; },
    hasError(state, action) { state.isLoading = false; state.error = action.payload; },
    setPage(state, action) {
      state.isLoading = false;
      state.items = action.payload.items || [];
      state.total = action.payload.total || 0;
      state.unacknowledgedTotal = action.payload.unacknowledgedTotal || 0;
    },
    setBadge(state, action) {
      state.badgeCount = action.payload || 0;
    },
    setAuditSummary(state, action) {
      state.auditSummary = action.payload || null;
    },
    // Quita un log de la lista local tras Ack o tras retry exitoso —
    // optimistic. Si el refetch del polling lo trae de nuevo, vuelve.
    removeItem(state, action) {
      state.items = state.items.filter((i) => i.id !== action.payload);
      state.total = Math.max(0, state.total - 1);
      state.unacknowledgedTotal = Math.max(0, state.unacknowledgedTotal - 1);
      state.badgeCount = Math.max(0, state.badgeCount - 1);
    },
  },
});

export default slice.reducer;

// Expuesta para hidratación desde el bootstrap del TPV.
export const { setBadge: setAeatErrorsBadge, setAuditSummary: setAeatAuditSummary } = slice.actions;

export function searchAeatErrors(query) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const res = await axios.post("/aeatErrors/search", query || {});
      dispatch(slice.actions.setPage(res.data || {}));
    } catch (err) {
      dispatch(slice.actions.hasError(err?.message || "Error"));
    }
  };
}

export function fetchAeatErrorsCount({ force = false } = {}) {
  return async (dispatch) => {
    if (!force && staleGuard("aeatErrors/count")) return;
    return dedupeInFlight("aeatErrors/count", async () => {
      try {
        const res = await axios.get("/aeatErrors/count");
        dispatch(slice.actions.setBadge(res.data?.count ?? 0));
      } catch (_) { /* silencio: badge no debe romper polling */ }
    });
  };
}

export function retryAeatError(idLog) {
  return async (dispatch) => {
    try {
      await axios.post(`/aeatErrors/retry/${idLog}`);
      // No removemos local todavía — el retry es async. El próximo refetch
      // ya recogerá el nuevo log si reaparece o lo quitará si entró OK.
      return true;
    } catch (err) {
      throw err;
    }
  };
}

export function retryAeatErrorsBulk(idLogs) {
  return async () => {
    const res = await axios.post(`/aeatErrors/retryBulk`, idLogs || []);
    return res.data?.enqueued ?? 0;
  };
}

export function acknowledgeAeatError(idLog) {
  return async (dispatch) => {
    await axios.post(`/aeatErrors/acknowledge/${idLog}`);
    dispatch(slice.actions.removeItem(idLog));
  };
}
